import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Select, SelectOptionType, SelectRef } from "@aidkitorg/component-library";
import { usePost } from "../../API";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";

type InlineTagsProps = {
  conversationItemId: string;
  tags: string[];
  onTagsUpdated?: (tags: string[]) => void;
  label?: string;
};

export function InlineTags({ conversationItemId, tags: initialTags, onTagsUpdated, label = "Tags" }: InlineTagsProps) {
  const addTag = usePost('/coach/conversation/tags/add');
  const removeTag = usePost('/coach/conversation/tags/remove');
  const getAllTags = usePost('/coach/conversation/tags/all');

  const [tags, setTags] = useState<string[]>(initialTags);
  const [availableTags, setAvailableTags] = useState<SelectOptionType[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [comboboxKey, setComboboxKey] = useState(0); // Key to force remount
  const ref = useRef<SelectRef>(null);
  const queryUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update local state when parent passes new tags
  useEffect(() => {
    setTags(initialTags);
  }, [initialTags]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (queryUpdateTimeoutRef.current) {
        clearTimeout(queryUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Load all available tags
  useAsyncEffect(async () => {
    const response = await getAllTags({});
    if (!('error' in response) && response.tags) {
      setAvailableTags(response.tags.map(tag => ({ value: tag, label: tag })));
    }
  }, []);

  // Compute available options: all tags that aren't already selected
  const baseOptions: SelectOptionType[] = useMemo(() => {
    return availableTags
      .filter(opt => !tags.includes(opt.value))
      .map(opt => ({ label: opt.label.toString(), value: opt.value }));
  }, [availableTags, tags]);

  // Add "Create new tag" option when user types something that doesn't exist
  const selectOptions: SelectOptionType[] = useMemo(() => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      return baseOptions;
    }

    // Check if exact match exists (case-insensitive)
    const exactMatch = baseOptions.some(
      opt => opt.value.toLowerCase() === trimmedQuery.toLowerCase()
    ) || tags.some(
      tag => tag.toLowerCase() === trimmedQuery.toLowerCase()
    );

    if (exactMatch) {
      return baseOptions;
    }

    // Add "Create new tag" option at the beginning with special marker
    return [
      { label: `Create "${trimmedQuery}"`, value: `__CREATE__${trimmedQuery}` },
      ...baseOptions
    ];
  }, [baseOptions, tags, query]);

  // Filter function that tracks the query with debouncing
  const filterFunction = useCallback((option: SelectOptionType, searchQuery: string) => {
    // Debounce query updates to avoid flashing
    if (searchQuery !== query) {
      if (queryUpdateTimeoutRef.current) {
        clearTimeout(queryUpdateTimeoutRef.current);
      }
      queryUpdateTimeoutRef.current = setTimeout(() => {
        setQuery(searchQuery);
      }, 50);
    }

    const trimmedQuery = searchQuery.trim().toLowerCase();

    // Always show the "Create..." option (identified by special marker)
    if (typeof option.value === 'string' && option.value.startsWith('__CREATE__')) {
      return true;
    }

    // Filter other options by value
    return option.value.toLowerCase().includes(trimmedQuery);
  }, [query]);

  const handleAddTag = useCallback(async (newTag: string) => {
    if (!newTag.trim()) return;

    setSaving(true);
    setError(null);

    try {
      const resp = await addTag({
        conversationItemId,
        tag: newTag.trim()
      });

      if (resp.alreadyExists) {
        setError('Tag already added.');
      } else if ('error' in resp) {
        setError(typeof resp.error === 'string' ? resp.error : 'Failed to add tag');
      } else {
        const updatedTags = [...tags, newTag.trim()];
        setTags(updatedTags);
        onTagsUpdated?.(updatedTags);

        // Add to available tags if not already there
        if (!availableTags.some(t => t.value === newTag.trim())) {
          setAvailableTags(prev => [...prev, { value: newTag.trim(), label: newTag.trim() }]);
        }
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add tag');
    } finally {
      setSaving(false);
      setQuery(""); // Clear query
      // Focus the select
      if (ref.current) {
        (ref.current as any).focus();
      }
    }
  }, [tags, availableTags, addTag, conversationItemId, onTagsUpdated]);

  const handleRemoveTag = useCallback(async (tagToRemove: string) => {
    setSaving(true);
    setError(null);

    try {
      const resp = await removeTag({
        conversationItemId,
        tag: tagToRemove
      });

      if ('error' in resp) {
        setError(typeof resp.error === 'string' ? resp.error : 'Failed to remove tag');
      } else {
        const updatedTags = tags.filter(t => t !== tagToRemove);
        setTags(updatedTags);
        onTagsUpdated?.(updatedTags);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to remove tag');
    } finally {
      setSaving(false);
    }
  }, [tags, removeTag, conversationItemId, onTagsUpdated]);

  return (
    <div className="py-2">
      <Select
        ref={ref}
        description="Select or type to add a tag"
        key={comboboxKey}
        label={label}
        options={selectOptions}
        value={undefined}
        searchable={true}
        onChange={(option) => {
          if (option) {
            // Extract the actual tag value (remove __CREATE__ marker if present)
            const tagValue = option.value.startsWith('__CREATE__')
              ? option.value.substring('__CREATE__'.length)
              : option.value;
            // Immediately add tag and force remount before the component shows the selected value
            Promise.resolve().then(() => handleAddTag(tagValue));
            // Increment key immediately to prevent flash of selected value
            setComboboxKey(prev => prev + 1);
          }
        }}
        filterFunction={filterFunction}
      />

      {/* Display selected tags as removable badges */}
      {tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
            >
              {tag}
              <button
                onClick={() => handleRemoveTag(tag)}
                disabled={saving}
                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Remove tag"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <span className="text-xs text-red-600 mt-1 block">{error}</span>
      )}
    </div>
  );
}
